import clsx from 'clsx'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import React, { memo, useEffect, useRef, useState } from 'react'

import { trackTileVariantInteraction } from '@Analytics/tileInteractions'
import AddToCart2SVG from '@Assets/SVG/AddToCart2'
import AngleLeftSVG from '@Assets/SVG/AngleLeft'
import AngleRightSVG from '@Assets/SVG/AngleRight'
import CheckRoundedSVG from '@Assets/SVG/CheckRounded'
import { Chip } from '@Components/Chips/Chip/Chip'
import { ImageV3, ImageV4 } from '@Components/Image'
import { Link } from '@Components/Link'
import { AddToWishList } from '@Components/WishList/addToWishList'
import { AssetsSlider, TILE_IMAGE_SIZES } from '@Domains/Product/Tiles/AssetsSlider'
import { ProductTileGroupEntry, ProductTileProps } from '@Domains/Product/Tiles/types'
import { useTranslation } from '@Globals/Translations'
import { Spinner } from '@Shared/Components/Spinner/Spinner'

import styles from './TileGrouped.module.scss'

// a single parent entry (no sibling tile data): the tile's own props drive
// the one swatch. Module-level so the array identity is stable per render.
const SELF_ONLY_GROUP: ProductTileGroupEntry[] = [{}]

interface GroupDotsProps {
  active: number
  // all members are shown (in the scroller) after the +N expander is used
  expanded: boolean
  // members beyond the 5-member display cap, shown as the +N expander
  hiddenCount: number
  items: {
    id: string
    // the member's lead image; a neutral fill renders when absent
    image?: string
    label: string
  }[]
  onExpand: () => void
  onSelect: (index: number) => void
  // first interaction with the tile (hover/touch/focus) flips the wrapper's
  // warmed state, which fills preloadTiles with the not-displayed siblings;
  // their lead images are then mounted invisibly so a dot click paints from
  // the browser cache instead of starting the fetch on swap
  onWarm: () => void
  preloadTiles: NonNullable<ProductTileGroupEntry['tile']>[]
}

const ProductTileContent = ({
  action,
  alreadyAddedToCart,
  assetURLs = [],
  beforePrice,
  boosted = false,
  bottomChips,
  brandTitle,
  bundleCombinedValue,
  chips,
  className,
  enableHover = true,
  groupDots,
  hoverImage,
  id,
  imageWidth,
  isOutOfStock = false,
  loadingBuyButton,
  disableBuyButton = loadingBuyButton,
  onBuy,
  price,
  primaryImage,
  priority = true,
  sourceID,
  thumbnailsVariant = false,
  tileIndex = 0,
  title,
  topChips,
  url,
  variantsColorCount,
}: ProductTileProps & { groupDots?: GroupDotsProps, thumbnailsVariant?: boolean }) => {
  const { t } = useTranslation()
  const [loaded, setLoaded] = useState(false)
  // true only after a real pointer entered the tile — the thumbnail
  // variants (b/c) mount the hover image on this instead of on image-load,
  // so touch devices never fetch hover assets and no desktop media query
  // (and its rerender) is needed
  const [pointerOver, setPointerOver] = useState(false)
  const useAssets = assetURLs.length > 1

  const groupDotsRef = useRef<HTMLDivElement>(null)
  // end-of-scroll state for the thumbnail nav arrows, mirroring the image
  // slider's dimmed disabled arrows
  const [thumbsAtStart, setThumbsAtStart] = useState(true)
  const [thumbsAtEnd, setThumbsAtEnd] = useState(false)

  const scrollThumbs = (offset: number) => (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
    const container = groupDotsRef.current
    if (container) {
      container.scrollBy({ behavior: 'smooth', left: offset * container.clientWidth })
    }
  }

  // NOTE on wheel behavior, so this doesn't get re-attempted: horizontal
  // gestures on the row leak their small vertical component into the page.
  // CSS cannot remove that leak — any non-auto overscroll-behavior-y also
  // kills page scrolling while the cursor rests on the row — and JS wheel
  // interception is not acceptable here. Native behavior is the decision.

  // the collapsed row always fits (6 slots, at most 5 thumbs + expander),
  // so the nav arrows only matter for the expanded row — and only when it
  // actually overflows: at-start and at-end simultaneously means everything
  // fits and there is nothing to slide
  const showThumbArrows = !!groupDots
    && groupDots.expanded
    && !(thumbsAtStart && thumbsAtEnd)

  // when the expander loads the rest, bring the newly selected first-new
  // thumbnail into view. Ref-guarded (not dep-array-driven) so later
  // selections don't re-yank the scroller.
  const scrolledToExpansion = useRef(false)
  useEffect(() => {
    if (!groupDots?.expanded || scrolledToExpansion.current) {
      return
    }

    scrolledToExpansion.current = true
    const container = groupDotsRef.current
    const target = container?.children[groupDots.active] as HTMLElement | undefined
    if (!container || !target) {
      return
    }

    // seed the end-state before the auto-scroll: a row whose expanded
    // content fits never fires onScroll, and at-start + at-end together is
    // what hides the arrows for it
    setThumbsAtEnd(container.scrollLeft + container.clientWidth >= container.scrollWidth - 1)

    // explicit scrollTo instead of scrollIntoView: the row's scroll-snap
    // (align: start) would pull an intoView-centered target back onto a
    // slot edge. Lands the new thumbnail at the parent's center, nudged
    // half a slot further so more of the newly loaded tail is visible.
    const targetLeft = target.getBoundingClientRect().left
      - container.getBoundingClientRect().left
      + container.scrollLeft
    const left = targetLeft - (container.clientWidth - target.clientWidth) / 2 + target.clientWidth / 2
    container.scrollTo({ behavior: 'smooth', left: Math.max(0, left) })
  }, [groupDots?.expanded, groupDots?.active])

  // the +N expander renders already at exactly 5 members, because the
  // narrow tier fits only 4 thumbs beside it (CSS hides it again in the
  // wide tier via moreNarrowOnly)
  const showMoreButton = !!groupDots
    && !groupDots.expanded
    && groupDots.hiddenCount >= 0

  // b/c: hover image mounts on real pointer interaction (no media query);
  // control keeps the historical loaded-then-desktop gating from the list
  const hoverImageReady = thumbnailsVariant
    ? pointerOver
    : loaded

  // b/c: one top row of at most 2 priority-ordered chips (the bottom row
  // already doesn't render there); control keeps the top/bottom split
  const effectiveTopChips = thumbnailsVariant
    ? chips
    : topChips

  const originalPrice = beforePrice || price
  const overwrittenPrice = beforePrice ? price : undefined
  // thumbnails variant (grouped_products_v2 c): bottom chips are not
  // rendered, and the brand moves to a line directly above the title
  const brandAboveTitle = Boolean(thumbnailsVariant && brandTitle)
  const showBrand = !!brandTitle && !brandAboveTitle
  const showVariants = !!variantsColorCount && variantsColorCount > 1

  const bottomChipsContent = map(bottomChips, chip => (
    <Chip key={`chip${'text' in chip ? chip.text : chip.translation}`} color={chip.color} icon={chip.icon}>
      {'text' in chip ? chip.text : t(...(chip.translation as unknown as any))}
    </Chip>
  ))

  // container queries cannot style the container itself, so the variant
  // moves the tile grid onto this inner wrapper and the tile root becomes
  // the query container (see .groupThumbsVariant in the stylesheet)
  const tileInner = (
    <>
      {isOutOfStock && (
        <div className={styles.outOfStock}>
          <p>{t('cms:OUT_OF_STOCK')}</p>
        </div>
      )}

      {useAssets && (
        <AssetsSlider
          assetURLs={assetURLs}
          className={styles.assets}
          id={id}
          onFirstImageLoad={() => setLoaded(true)}
          priority={priority}
          slideClassName={styles.groupedSlide}
          tileIndex={tileIndex}
          title={title}
        />
      )}

      {!useAssets && (
        <>
          {!priority && primaryImage && (
            <ImageV4
              alt={title}
              className={clsx(styles.images, styles.image)}
              decoding="async"
              draggable={false}
              loading="lazy"
              onLoad={() => setLoaded(true)}
              src={primaryImage}
              style={{
                objectFit: 'contain',
              }}
              title={title}
              width={imageWidth || (priority || enableHover) ? 400 : 180}
            />
          )}

          {priority && primaryImage && (
            <ImageV3
              alt={title}
              className={clsx(styles.images, styles.image)}
              decoding="async"
              draggable={false}
              fetchPriority="high"
              fill
              loading="eager"
              onLoad={() => setLoaded(true)}
              priority
              sizes={TILE_IMAGE_SIZES}
              src={primaryImage}
              style={{
                objectFit: 'contain',
              }}
              title={title}
            />
          )}

          {hoverImage && hoverImageReady && enableHover && (
            <ImageV4
              alt={`hover: ${title}`}
              className={clsx(styles.images, styles.imageHover)}
              decoding="async"
              draggable={false}
              loading="lazy"
              src={hoverImage}
              style={{
                objectFit: 'cover',
              }}
              title={`hover: ${title}`}
              width={imageWidth || enableHover ? 500 : 180}
            />
          )}
        </>
      )}

      {brandAboveTitle
        ? (
          <div className={styles.titleWithBrand}>
            <p className={styles.titleBrand}>{brandTitle}</p>
            <p className={styles.title}>{title}</p>
          </div>
        )
        : <p className={styles.title}>{title}</p>}

      {!isEmpty(effectiveTopChips) && (
        <div className={styles.topChips}>
          {map(effectiveTopChips, chip => (
            <Chip key={`chip${'text' in chip ? chip.text : chip.translation}`} color={chip.color} icon={chip.icon}>
              {'text' in chip ? chip.text : t(...(chip.translation as unknown as any))}
            </Chip>
          ))}
        </div>
      )}

      {!isEmpty(bottomChips) && !thumbnailsVariant && (
        <div className={styles.bottomChips}>
          {bottomChipsContent}
        </div>
      )}

      <div className={styles.price}>
        <p className={clsx(overwrittenPrice && styles.strikeThrough)}>{originalPrice}</p>
        <p>{overwrittenPrice}</p>
      </div>

      {bundleCombinedValue && (
        <div className={styles.bundle}>
          {`${t('common:BUNDLE_COMBINED_VALUE')} - ${bundleCombinedValue}`}
        </div>
      )}

      {action && (
        <div className={styles.actions}>
          {action}
        </div>
      )}

      {!!onBuy && (
        <div className={styles.actions}>
          <button
            className={clsx(styles.buyButton, alreadyAddedToCart && styles.success, sourceID)}
            disabled={disableBuyButton}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onBuy(id)
            }}
            type="button"
          >
            {' '}
            {loadingBuyButton && <Spinner className={styles.addToCartSpinner} />}
            {(!loadingBuyButton && !alreadyAddedToCart) && <AddToCart2SVG />}
            {(!loadingBuyButton && alreadyAddedToCart) && <CheckRoundedSVG className={styles.alreadyAddedIcon} />}
          </button>
        </div>
      )}

      <AddToWishList
        className={styles.wish}
        id={id}
        sourceID={sourceID}
      />

      {!!(showBrand || showVariants || groupDots) && (
        <div className={styles.brandVariants}>
          {groupDots && (
            <div className={styles.groupDotsWrapper}>
              {showThumbArrows && (
                <button
                  aria-label="Previous"
                  className={clsx(styles.groupDotArrow, styles.left)}
                  disabled={thumbsAtStart}
                  onClick={scrollThumbs(-1)}
                  tabIndex={-1}
                  type="button"
                >
                  <AngleLeftSVG />
                </button>
              )}
              <div
                className={clsx(
                  styles.groupDots,
                  groupDots.expanded && styles.groupExpanded,
                )}
                onScroll={(event) => {
                  const { clientWidth, scrollLeft, scrollWidth } = event.currentTarget
                  setThumbsAtStart(scrollLeft <= 1)
                  setThumbsAtEnd(scrollLeft + clientWidth >= scrollWidth - 1)
                }}
                ref={groupDotsRef}
              >
                {groupDots.items.map((item, index) => (
                  <button
                    key={item.id}
                    aria-label={item.label}
                    aria-pressed={index === groupDots.active}
                    className={clsx(styles.groupDot, index === groupDots.active && styles.activeGroupDot)}
                    onClick={(event) => {
                      event.preventDefault()
                      event.stopPropagation()
                      groupDots.onSelect(index)
                    }}
                  // thumbnails select on hover too; click stays for touch.
                  // Tap-synthesized mouseenter just pre-selects what the
                  // click selects anyway, so no touch gating is needed.
                    onMouseEnter={() => groupDots.onSelect(index)}
                    type="button"
                  >
                    <span className={styles.groupDotBox}>
                      {item.image
                        ? (
                          <ImageV4
                            alt=""
                            decoding="async"
                            draggable={false}
                            loading="lazy"
                            src={item.image}
                            style={{
                              objectFit: 'contain',
                            }}
                            width={56}
                          />
                        )
                        : <span className={styles.groupDotFill} />}
                    </span>
                  </button>
                ))}
                {showMoreButton && (
                  <button
                    className={clsx(
                      styles.groupDot,
                      styles.groupDotMore,
                      groupDots.hiddenCount === 0 && styles.moreNarrowOnly,
                    )}
                    onClick={(event) => {
                      event.preventDefault()
                      event.stopPropagation()
                      groupDots.onExpand()
                    }}
                    type="button"
                  >
                    {/* mirrors the PDP linked-products trigger: a bare +N
                      needs no translation. Both tier counts render; the
                      row's container width picks one. */}
                    <span className={styles.groupDotBox}>
                      {groupDots.hiddenCount > 0 && (
                        <span className={styles.moreCountWide}>{`+${groupDots.hiddenCount}`}</span>
                      )}
                      <span className={styles.moreCountNarrow}>{`+${groupDots.hiddenCount + 1}`}</span>
                    </span>
                  </button>
                )}
              </div>
              {showThumbArrows && (
                <button
                  aria-label="Next"
                  className={clsx(styles.groupDotArrow, styles.right)}
                  disabled={thumbsAtEnd}
                  onClick={scrollThumbs(1)}
                  tabIndex={-1}
                  type="button"
                >
                  <AngleRightSVG />
                </button>
              )}
            </div>
          )}
          {!groupDots && showVariants && (
            <span>
              <p className={styles.variants}>{`${variantsColorCount} ${t('products:COLOURS_LABEL')}`}</p>
            </span>
          )}
          {showBrand && <p className={styles.brand}>{brandTitle}</p>}
        </div>
      )}

      {groupDots && groupDots.preloadTiles.length !== 0 && (
        <div aria-hidden className={styles.groupPreload}>
          {groupDots.preloadTiles.map((tile) => {
            const src = tile.assetURLs?.[0] || tile.primaryImage
            if (!src) {
              return null
            }

            // mirror the paths the swapped-in tile will render with, so the
            // warmed URL is the one the real image requests: the slider's
            // first slide and the priority single image are ImageV3 (srcset
            // picked from sizes, identical attrs → identical URL even at
            // 1px), the rest is ImageV4 at its fixed 400px width
            if ((tile.assetURLs?.length || 0) > 1 || priority) {
              return (
                <ImageV3
                  key={tile.id}
                  alt=""
                  decoding="async"
                  fetchPriority="low"
                  fill
                  loading="eager"
                  sizes={TILE_IMAGE_SIZES}
                  src={src}
                  style={{
                    objectFit: 'contain',
                  }}
                />
              )
            }

            return (
              <ImageV4
                key={tile.id}
                alt=""
                decoding="async"
                fetchPriority="low"
                loading="eager"
                src={src}
                style={{
                  objectFit: 'contain',
                }}
                width={400}
              />
            )
          })}
        </div>
      )}
    </>
  )

  return (
    <Link
      analytics={{
        source_id: sourceID,
        dest_page_type: 'product',
        dest_page_type_id: id,
      }}
      className={clsx(
        'th-p',
        className,
        styles.ProductTile,
        priority && styles.priority,
        (!!onBuy || !!action) && styles.withActions,
        !!hoverImage && enableHover && styles.hasHover,
        thumbnailsVariant && styles.groupThumbsVariant,
        boosted && 'th-b',
      )}
      href={url}
      id={id}
      onFocus={groupDots?.onWarm}
      onPointerOver={(event) => {
        // touch browsers synthesize mouse-style events after a tap, which
        // would mount (and download) the hover asset on touch devices;
        // pointer events carry the input type, so real hovers can be told
        // apart. Touch tiles still warm via onTouchStart.
        if (event.pointerType === 'touch') {
          return
        }

        setLoaded(true)
        setPointerOver(true)
        groupDots?.onWarm()
      }}
      onTouchStart={groupDots?.onWarm}
    >
      {thumbnailsVariant
        ? <div className={styles.tileLayout}>{tileInner}</div>
        : tileInner}
    </Link>
  )
}

// the grouped_products_v2 (b/c) tile: shows one thumbnail per colour-group
// member (parent first); selecting one replaces the whole tile's display
// data with that member's, in place — deliberately NOT keyed/remounted, so
// persistent chrome like the slider arrows doesn't replay its hover
// fade-in on every selection; the slider rewinds its own state on swap.
// b and c differ upstream only in whether the result set is collapsed via
// Algolia distinct (b) or every group member gets its own tile (c).
export const ProductTileGrouped = memo((props: ProductTileProps) => {
  const [activeGroupIndex, setActiveGroupIndex] = useState(0)
  // warmed survives swaps (the content below remounts on every selection);
  // once true, the not-displayed siblings' lead images stay mounted hidden
  const [warmed, setWarmed] = useState(false)
  // groups are uncapped in the data; display (and image loading) caps at 5
  // members until the +X button expands the rest
  const [expandedGroup, setExpandedGroup] = useState(false)
  const { groupedTiles, ...rest } = props

  // an ungrouped tile still renders the row, with the product itself as its
  // only thumbnail, so tile heights and layout stay consistent with the
  // grouped tiles around it
  const effectiveGroupedTiles = groupedTiles && groupedTiles.length !== 0
    ? groupedTiles
    : SELF_ONLY_GROUP

  const activeEntry = effectiveGroupedTiles[activeGroupIndex]
  // collapsed: everything shows when the group fits both the 5-member cap
  // and the breakpoint's columns; otherwise the thumbs yield one slot to
  // the +N button. The visible members are a prefix of the full list, so
  // selection indices stay valid across expansion.
  const groupTotal = effectiveGroupedTiles.length
  const visibleGroupedTiles = expandedGroup ? effectiveGroupedTiles : effectiveGroupedTiles.slice(0, 5)

  return (
    <ProductTileContent
      {...rest}
      {...activeEntry?.tile}
      groupDots={{
        active: activeGroupIndex,
        expanded: expandedGroup,
        hiddenCount: groupTotal - 5,
        items: visibleGroupedTiles.map(entry => ({
          id: entry.tile?.id || rest.id,
          image: entry.tile
            ? entry.tile.primaryImage
            : rest.primaryImage,
          label: entry.tile?.title || rest.title,
        })),
        onExpand: () => {
          // the expansion also selects a member (below), so it counts as a
          // variant interaction like every other click/hover selection
          trackTileVariantInteraction(rest.tileIndex || 0)
          setExpandedGroup(true)
          // select the first member the expansion reveals (index 5 past the
          // display cap, or the last member for groups of exactly 5), so
          // the click gives immediate feedback in the tile
          setActiveGroupIndex(Math.min(5, groupTotal - 1))
        },
        onSelect: (index: number) => {
          // every click/hover selection counts, same-index re-selects
          // included, per the plan's "variant interactions" definition
          trackTileVariantInteraction(rest.tileIndex || 0)
          setActiveGroupIndex(index)
        },
        onWarm: () => setWarmed(true),
        preloadTiles: warmed
          ? visibleGroupedTiles.flatMap((entry, index) => (index !== activeGroupIndex && entry.tile ? [entry.tile] : []))
          : [],
      }}
      thumbnailsVariant
    />
  )
}, (prev, next) => (
  prev.className === next.className
  && prev.enableHover === next.enableHover
  && prev.loadingBuyButton === next.loadingBuyButton
  && prev.disableBuyButton === next.disableBuyButton
  && prev.isOutOfStock === next.isOutOfStock
  && prev.onBuy === next.onBuy
  && prev.action === next.action
))
